import { memo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';
import {
  ProductionStepDetails,
  ProductionStepNormalization,
} from 'benchmarking-domain/normalization/normalization-details';
import ProjectProgressStatistics from 'benchmarking-domain/project-progress-statistics';
import { DataGridInMemory } from 'components/core/shared/data-grid-in-memory';

import { NormalizationGridColumns } from './normalization-grid-columns';

interface NormalizationStepAccordionProps {
  productionStep: ProductionStepNormalization;
  projectProgressStatistics?: ProjectProgressStatistics;
}

export const NormalizationStepAccordion: React.FC<NormalizationStepAccordionProps> = memo(
  ({ productionStep, projectProgressStatistics }) => {
    const [expanded, setExpanded] = useState<boolean>(false);

    const getStatus = (sectionId: string) => {
      const numberOfInputsProvided = projectProgressStatistics?.countInSections?.[sectionId] ?? 0;
      const totalNumberOfInputs = projectProgressStatistics?.totalInSections?.[sectionId] ?? 0;

      if (numberOfInputsProvided === 0) {
        return 'Not started';
      }

      if (numberOfInputsProvided >= totalNumberOfInputs) {
        return 'Completed';
      }

      return 'In progress';
    };

    const initialState: GridInitialStateCommunity = {
      pagination: { paginationModel: { pageSize: 100 } },
    };

    return (
      <Accordion onChange={(_, expanded) => setExpanded(expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1">{productionStep.sectionName}</Typography>
            <Typography variant="body1" sx={{ marginRight: 3 }}>
              {getStatus(productionStep.sectionId)}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {expanded && (
            <DataGridInMemory
              getRowId={(r: ProductionStepDetails) => r.variable}
              columns={NormalizationGridColumns}
              showQuickSearch={false}
              checkboxSelection={false}
              getRowHeight={() => 'auto'}
              rows={productionStep.normalizationData}
              initialState={initialState}
              hideFooter={true}
              hideFooterPagination={true}
              showCellVerticalBorder={true}
              showColumnVerticalBorder={true}
              sx={{
                '& .MuiDataGrid-cell': {
                  padding: '10px',
                },
              }}
            />
          )}
        </AccordionDetails>
      </Accordion>
    );
  }
);
